import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, ExternalLink, Navigation } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Badge } from "@tabipla/ui";
import type { MapViewProps } from "./types";
import { getGoogleMapsUrl, getGoogleMapsDirectionsUrl } from "@/utils/google-maps";
import { getCategoryIconComponent, getCategoryBadgeColors } from "@/domain/constants/categories";

export function MapView({ itineraries, onSpotClick }: MapViewProps) {
  const { t } = useTranslation();

  const allSpots = useMemo(() => {
    return itineraries.flatMap((i) => i.spots);
  }, [itineraries]);

  if (allSpots.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <MapPin className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("map.noSpots")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full space-y-4 overflow-hidden">
      {itineraries.map((itinerary) => {
        const directionsUrl = getGoogleMapsDirectionsUrl(itinerary.spots);

        return (
          <Card key={itinerary.dayNumber} className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base">
                  {t("trips.dayNumber", { day: itinerary.dayNumber })}
                </CardTitle>
                {directionsUrl && (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    <Navigation className="h-3.5 w-3.5" />
                    {t("map.openRoute", "ルートを開く")}
                  </a>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {itinerary.spots.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {t("spots.noSpots")}
                </p>
              ) : (
                <div className="space-y-2">
                  {itinerary.spots.map((spot, index) => {
                    const CategoryIcon = getCategoryIconComponent(spot.category);
                    return (
                      <div
                        key={spot.id}
                        className="flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50"
                      >
                        {/* Order number */}
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                          {index + 1}
                        </div>

                        {/* Content */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div
                              className="min-w-0 flex-1 cursor-pointer"
                              onClick={() => onSpotClick?.(spot)}
                            >
                              <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <Badge
                                  variant="secondary"
                                  className={`shrink-0 ${getCategoryBadgeColors(spot.category)}`}
                                >
                                  {CategoryIcon ? (
                                    <CategoryIcon className="h-4 w-4" />
                                  ) : (
                                    <MapPin className="h-4 w-4" />
                                  )}
                                </Badge>
                                <span className="break-words font-medium">{spot.name}</span>
                              </div>
                              {spot.startTime && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {spot.startTime}
                                  {spot.endTime && ` - ${spot.endTime}`}
                                </p>
                              )}
                            </div>

                            {/* Google Maps link */}
                            {spot.location && (
                              <a
                                href={getGoogleMapsUrl(spot)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors hover:bg-primary/20 active:bg-primary/30"
                                title={t("map.openInMaps", "Google Mapsで開く")}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                          </div>

                          {/* Location text */}
                          {spot.location && (
                            <a
                              href={getGoogleMapsUrl(spot)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 flex min-w-0 items-start gap-1 text-xs text-primary hover:underline"
                            >
                              <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
                              <span className="break-words">{spot.location}</span>
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
